import { WORLD } from '../../core/config';
import { layoutToWorld } from '../../core/coords';
import { fromRing, intersection } from '../../geo/clip';
import type { Vec2 } from '../../geo/types';
import { bridgeProfile, DIKE_BRIDGE_ID } from '../../site/bridgeHeight';
import type { BridgeDeck } from '../../site/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { buildArchDressings } from './archDressings';
import { stationsOf, type ArchBridgeSpec } from './archBridge';
import { buildParapets } from './bridgeParapets';
import { buildBridgeSolid } from './bridgeSolid';
import { estimatedVaults, type EstimatedVaults } from './vaultEstimate';

// These named crossings retain their source masonry readings. Other unknown crossings
// keep their existing reconstruction rather than acquiring an invented arch.
export const MASONRY_ARCH_BRIDGES = new Set([
  'bridge-way-38314621',
  'bridge-way-38314629',
  DIKE_BRIDGE_ID,
]);
const ABUTMENT_M = 0.8;
const CROWN_COVER_M = 0.45;

// The dike crossing keeps the one portal the painting shows; the burgwal crossings' vaults are
// estimated.
function vaultsOver(bridge: BridgeDeck, wet: number): EstimatedVaults {
  return bridge.id === DIKE_BRIDGE_ID
    ? { span: wet, pierWidth: ABUTMENT_M, crownsAt: [wet / 2] }
    : estimatedVaults(wet);
}

/** Estimated segmental vaults inside the full-width wet span, with substantial bank piers.
 * The source establishes masonry arches, not their number, concealed sections or dimensions. */
export function masonryBridgeSpec(bridge: BridgeDeck): ArchBridgeSpec {
  const profile = bridgeProfile(bridge);
  const middle = (profile.from + profile.to) / 2;
  const xs = bridge.deck.flatMap((deck) => deck.outer.map(profile.along));
  // At skew banks, the outermost deck corners do not delimit a full-width opening.
  // Put the springings beyond the inner bank corners, leaving solid support at both ends.
  const left = Math.max(...xs.filter((x) => x < middle)) + ABUTMENT_M;
  const right = Math.min(...xs.filter((x) => x > middle)) - ABUTMENT_M;
  const vaults = vaultsOver(bridge, right - left);
  // A vault under a ramp crowns lower than the one under the crest, each the same cover below
  // the deck over its own crown.
  const crowns = vaults.crownsAt.map((x) => profile.height(left + x) - CROWN_COVER_M);
  return {
    length: profile.to - profile.from,
    width: bridge.width,
    spans: crowns.map(() => vaults.span),
    pierWidth: vaults.pierWidth,
    firstArchAt: left - profile.from,
    springY: WORLD.waterY - 0.1,
    crownY: profile.height(middle) - CROWN_COVER_M,
    crowns,
    footY: -2.5,
    parapet: { height: 0, thickness: 0 },
    deckY: (x) => profile.height(profile.from + x),
  };
}

/** Cut the actual bank-clipped deck at the arch's curve stations. Each piece is a closed
 * masonry solid between its walking top and soffit, rather than filling the water portal. */
export function buildMasonryBridge(
  builder: MeshBuilder,
  stone: VertexStyle,
  bridge: BridgeDeck,
  sides: readonly { a: Vec2; b: Vec2 }[]
): boolean {
  if (bridge.kind !== 'stone-arch' || !MASONRY_ARCH_BRIDGES.has(bridge.id)) return false;
  const profile = bridgeProfile(bridge);
  const spec = masonryBridgeSpec(bridge);
  const stations = stationsOf(spec);
  const at = (x: number, across: number): Vec2 => [
    bridge.a[0] + profile.axis[0] * (profile.from + x) - profile.axis[1] * across,
    bridge.a[1] + profile.axis[1] * (profile.from + x) + profile.axis[0] * across,
  ];
  for (let k = 1; k < stations.length; k++) {
    const a = stations[k - 1]!,
      b = stations[k]!;
    const band = fromRing([
      at(a.x, -bridge.width),
      at(b.x, -bridge.width),
      at(b.x, bridge.width),
      at(a.x, bridge.width),
    ]);
    const bottom = (p: Vec2) => {
      const t = Math.max(0, Math.min(1, (profile.along(p) - profile.from - a.x) / (b.x - a.x)));
      return a.after + (b.before - a.after) * t;
    };
    const top = (p: Vec2) => profile.height(profile.along(p));
    for (const piece of intersection(bridge.deck, [band]))
      buildBridgeSolid(builder, stone, piece, bottom, top);
  }
  const frame: Frame = {
    origin: layoutToWorld(at(0, 0), 0),
    yaw: Math.atan2(profile.axis[1], profile.axis[0]),
  };
  // Individual physical voussoirs expose the curve at both water-facing portals.
  buildArchDressings(builder, stone, frame, spec);
  buildParapets(builder, stone, sides, profile);
  return true;
}
